import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import type { Ticket, TicketStatus, TicketCategory } from '@/components/tickets/types';
import type { User } from '@/components/users/types';

export interface TicketStats {
  totalTickets: number;
  openTickets: number;
  resolvedByAI: number;
  percentResolvedByAI: number;
  avgResolutionTimeMs: number | null;
}

export function useTicketStats() {
  return useQuery<TicketStats>({
    queryKey: ['ticket-stats'],
    queryFn: () => axios.get('/api/tickets/stats').then((r) => r.data),
  });
}

export interface DailyTicketCount {
  day: string;
  count: number;
}

export function useTicketDailyCounts() {
  return useQuery<{ data: DailyTicketCount[] }>({
    queryKey: ['ticket-daily-counts'],
    queryFn: () => axios.get('/api/tickets/daily-counts').then((r) => r.data),
  });
}

export function useTicket(id: string | undefined) {
  return useQuery({
    queryKey: ['ticket', id],
    queryFn: async () => {
      const res = await axios.get<{ ticket: Ticket }>(`/api/tickets/${id}`);
      return res.data.ticket;
    },
    enabled: !!id,
  });
}

export interface UseTicketsParams {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  statusFilter: TicketStatus | 'ALL';
  categoryFilter: TicketCategory | 'ALL';
  page: number;
  pageSize: number;
}

export function useTickets({
  sortBy,
  sortOrder,
  statusFilter,
  categoryFilter,
  page,
  pageSize,
}: UseTicketsParams) {
  return useQuery<{ tickets: Ticket[]; total: number; page: number; pageSize: number }>({
    queryKey: ['tickets', sortBy, sortOrder, statusFilter, categoryFilter, page],
    queryFn: () =>
      axios
        .get('/api/tickets', {
          params: {
            sortBy,
            sortOrder,
            page,
            pageSize,
            ...(statusFilter !== 'ALL' && { status: statusFilter }),
            ...(categoryFilter !== 'ALL' && { category: categoryFilter }),
          },
        })
        .then((r) => r.data),
  });
}

export function useUpdateTicket(ticketId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: { status?: TicketStatus; category?: TicketCategory | null }) =>
      axios.patch(`/api/tickets/${ticketId}`, patch),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: ['ticket', ticketId] });
      const previous = queryClient.getQueryData<Ticket>(['ticket', ticketId]);
      queryClient.setQueryData<Ticket>(['ticket', ticketId], (old) =>
        old ? { ...old, ...patch } : old,
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(['ticket', ticketId], context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
    },
  });
}

export function useSummarizeTicket(ticketId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await axios.post<{ ticket: Ticket }>(`/api/tickets/${ticketId}/summarize`);
      return res.data.ticket;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
    },
  });
}

export function useAssignTicket(ticketId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assignedAgentId: string | null) =>
      axios.patch(`/api/tickets/${ticketId}`, { assignedAgentId }),
    onMutate: async (assignedAgentId) => {
      await queryClient.cancelQueries({ queryKey: ['ticket', ticketId] });
      const previous = queryClient.getQueryData<Ticket>(['ticket', ticketId]);
      const users = queryClient.getQueryData<{ users: User[] }>(['users'])?.users;
      const agent = assignedAgentId
        ? (users?.find((u) => u.id === assignedAgentId) ?? null)
        : null;
      queryClient.setQueryData<Ticket>(['ticket', ticketId], (old) =>
        old
          ? {
              ...old,
              assignedAgentId,
              assignedAgent: agent
                ? { id: agent.id, name: agent.name, email: agent.email }
                : null,
            }
          : old,
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous)
        queryClient.setQueryData(['ticket', ticketId], context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
    },
  });
}
